import { useState } from 'react';
import { format, isValid } from 'date-fns';
import {
  BadgeCheck,
  Bot,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Globe,
  Hand,
  Landmark,
  Link,
  PencilLine,
  LayoutList,
  MapPin,
  MinusCircle,
  PlusCircle,
  ShoppingBag,
  SmilePlus,
  Store,
  Tag,
  TicketPercent,
  UserCheck,
  Wallet,
  Wrench,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { CartEditedByOperatorEventPayload, ConversationEvent, ConversationEventType } from '@/lib/gupshup-client';

const ICON_BY_TYPE: Record<ConversationEventType, LucideIcon> = {
  order_created: ShoppingBag,
  order_paid: CreditCard,
  order_payment_failed: XCircle,
  takeover_started: UserCheck,
  handoff_requested: Hand,
  ai_resumed: Bot,
  flow_sent: LayoutList,
  flow_completed: LayoutList,
  agent_tool_called: Wrench,
  flow_screen_navigated: LayoutList,
  tags_changed: Tag,
  sentiment_changed: SmilePlus,
  item_added: PlusCircle,
  item_removed: MinusCircle,
  delivery_address_set: MapPin,
  payment_method_selected: Wallet,
  location_auto_selected: Store,
  bank_transfer_received: Landmark,
  transfer_manually_verified: BadgeCheck,
  cart_edited_by_operator: PencilLine,
  coupon_applied: TicketPercent,
  coupon_removed: TicketPercent,
  web_checkout_link_sent: Link,
  web_checkout_opened: Globe,
};

/**
 * Event types whose payload carries the shared `source` marker (agent / Flow / web). Only these may
 * grow the "vía web" suffix — `bank_transfer_received` also has a `source` field, but there it
 * names the transfer's attribution, not the ordering surface.
 */
const ORDER_SOURCE_EVENT_TYPES = new Set<ConversationEventType>([
  'order_created',
  'item_added',
  'item_removed',
  'delivery_address_set',
  'payment_method_selected',
  'coupon_applied',
  'coupon_removed',
]);

function formatEventTime(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    return isValid(date) ? format(date, 'HH:mm') : '';
  } catch {
    return '';
  }
}

/**
 * What an operator's cart edit actually did, as a one-line suffix.
 *
 * Rows written before the before/after snapshot existed carry only `changed` — say which sections
 * were touched and stop there, rather than inventing a "0 → 0" that reads like a real diff.
 */
function describeCartEdit(
  payload: CartEditedByOperatorEventPayload,
  t: ReturnType<typeof useTranslation<'conversations'>>['t'],
): string {
  const parts: string[] = [];

  if (payload.changed.length > 0) {
    const sections = payload.changed.map((section) => t(`events.cartEdit.${section}` as const)).join(', ');
    parts.push(t('events.cartEdit.sections', { sections }));
  }

  if (payload.before && payload.after && payload.before.total !== payload.after.total) {
    parts.push(t('events.cartEdit.totalChange', { from: payload.before.total, to: payload.after.total }));
  }

  if (payload.requiresCustomerApproval) {
    parts.push(t('events.cartEdit.pendingApproval'));
  }

  return parts.join('');
}

/**
 * Centered chip row for a conversation timeline event, interleaved between chat bubbles.
 * Advanced-visibility events render muted/smaller; agent_tool_called supports expanding
 * the (truncated) tool input.
 */
export function ConversationEventRow({ event }: { event: ConversationEvent }) {
  const { t } = useTranslation('conversations');
  const [expanded, setExpanded] = useState(false);

  const Icon = ICON_BY_TYPE[event.type] ?? Bot;
  const isAdvanced = event.visibility === 'advanced';

  // Interpolation vars for the label. Start from the raw payload (covers {{tool}}, {{total}}, …)
  // and derive friendlier values for the order/delivery events whose payloads aren't scalar.
  const vars: Record<string, unknown> = { ...(event.payload ?? {}) };
  if (event.type === 'item_added' && event.payload) {
    vars.summary = event.payload.items.map((i) => `${i.quantity}× ${i.name}`).join(', ');
    vars.count = event.payload.items.length;
  } else if (event.type === 'delivery_address_set' && event.payload) {
    vars.outcome = t(`events.deliveryOutcome.${event.payload.outcome}`, { defaultValue: event.payload.outcome });
    vars.addressSuffix = event.payload.address ? ` · ${event.payload.address}` : '';
  } else if (event.type === 'payment_method_selected' && event.payload) {
    vars.method = event.payload.savedCardLabel ?? event.payload.method;
  } else if (event.type === 'location_auto_selected' && event.payload) {
    vars.locationName = event.payload.locationName ?? event.payload.locationId;
  } else if (event.type === 'cart_edited_by_operator' && event.payload) {
    vars.detail = describeCartEdit(event.payload, t);
  } else if (event.type === 'order_created') {
    vars.approvalSuffix = event.payload?.confirmedWithoutCustomerApproval
      ? t('events.orderCreatedWithoutApproval')
      : '';
  } else if (event.type === 'transfer_manually_verified' && event.payload) {
    // Two opposite facts under one type — the action carries the sentence, so the row cannot read as
    // "confirmed" for a confirmation that was taken back.
    vars.action = t(event.payload.reverted ? 'events.transferOverride.reverted' : 'events.transferOverride.set');
    const noReceipt =
      !event.payload.reverted && !event.payload.hadReceipt ? t('events.transferOverride.noReceipt') : '';
    vars.detail = `${noReceipt}${event.payload.reason ? ` · ${event.payload.reason}` : ''}`;
  } else if (event.type === 'web_checkout_opened') {
    vars.locationSuffix = event.payload?.locationName ? ` · ${event.payload.locationName}` : '';
  } else if (event.type === 'ai_resumed') {
    vars.detail = !event.payload?.withMessage
      ? // Handed back with nothing said. Worth naming, because it is the case where the message the
        // customer receives next is the agent's own doing, not the operator's.
        event.payload?.agentTurnQueued
        ? t('events.aiResumedWithAgentTurn')
        : ''
      : event.payload.buttonCount
        ? t('events.aiResumedWithButtons')
        : t('events.aiResumedWithMessage');
  }

  // The ordering-surface marker, appended uniformly rather than baked into every label: the same
  // "item added" means an agent tool call, an in-Flow edit, or a tap on the web page — the operator
  // reading a timeline of page taps with no chat bubbles needs to see which one it was.
  const orderSource = ORDER_SOURCE_EVENT_TYPES.has(event.type)
    ? (event.payload as { source?: 'flow' | 'web' } | undefined)?.source
    : undefined;

  const label =
    t(`events.${event.type}`, {
      defaultValue: t('events.unknown', { type: event.type }),
      ...vars,
    }) + (orderSource ? ` · ${t(`events.source.${orderSource}`)}` : '');

  const toolInput = event.type === 'agent_tool_called' ? event.payload?.input : undefined;
  // The agent's own summary of why it handed off — shown inline under the label as a mini-summary
  // so the operator taking over sees the context without opening anything.
  const handoffReason = event.type === 'handoff_requested' ? event.payload?.reason?.trim() : undefined;

  return (
    <div className="flex justify-center my-2 animate-in fade-in duration-200">
      <div
        className={cn(
          'inline-flex max-w-[85%] flex-col rounded-full border px-3 py-1 shadow-sm',
          isAdvanced
            ? 'bg-chat-panel/60 border-chat-border-soft text-chat-text-muted'
            : 'bg-chat-panel border-chat-border text-chat-text',
          (expanded || handoffReason) && 'rounded-lg',
        )}
      >
        <div className={cn('flex min-w-0 items-center gap-1.5', isAdvanced ? 'text-[11px]' : 'text-xs')}>
          <Icon className={cn('shrink-0', isAdvanced ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
          {/* Wraps (clamped) rather than truncating on one line: a nowrap label makes this chip's
              min-content width the whole address or tool name, which on mobile widens the entire
              message pane and pushes the conversation off-screen. */}
          <span className="line-clamp-2 min-w-0 break-words">{label}</span>
          <span className="text-[10px] text-chat-text-muted shrink-0">{formatEventTime(event.createdAt)}</span>
          {toolInput && (
            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              className="shrink-0 text-chat-text-muted hover:text-chat-text"
              aria-label={t('events.toggleDetailsAria')}
            >
              {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
          )}
        </div>
        {handoffReason && (
          <p className="mt-1 pl-5 text-left text-[11px] text-chat-text-muted break-words">{handoffReason}</p>
        )}
        {expanded && toolInput && (
          <pre className="mt-1 max-w-full overflow-x-auto whitespace-pre-wrap break-all text-left text-[10px] text-chat-text-muted">
            {toolInput}
          </pre>
        )}
      </div>
    </div>
  );
}
